import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { Navigation } from './components/Navigation';
import { Dashboard } from './components/Dashboard';
import { ContractForm } from './components/ContractForm';
import { ContractsList } from './components/ContractsList';
import { useContracts } from './hooks/useContracts';
import { Contract } from './types/Contract';
import './App.css';

const AppContent: React.FC = () => {
  const { contracts, loading, error, saveContract, updateContract, deleteContract, updateContractStatus } = useContracts();
  const [editingContract, setEditingContract] = useState<Contract | null>(null);
  const [operationLoading, setOperationLoading] = useState(false);
  const navigate = useNavigate();

  const handleCreateContract = async (contract: Contract) => {
    try {
      setOperationLoading(true);
      await saveContract(contract);
      navigate('/history');
      console.log('✅ Новый договор создан и сохранен');
    } catch (error) {
      console.error('❌ Ошибка создания договора:', error);
      alert('Ошибка при создании договора. Попробуйте еще раз.');
    } finally {
      setOperationLoading(false);
    }
  };

  const handleUpdateContract = async (contract: Contract) => {
    try {
      setOperationLoading(true);
      await updateContract(contract.id, contract);
      setEditingContract(null);
      navigate('/history');
      console.log('✅ Договор обновлен');
    } catch (error) {
      console.error('❌ Ошибка обновления договора:', error);
      alert('Ошибка при обновлении договора. Попробуйте еще раз.');
    } finally {
      setOperationLoading(false);
    }
  };

  const handleEditContract = (contract: Contract) => {
    setEditingContract(contract);
    navigate('/create');
  };

  const handleDeleteContract = async (id: string) => {
    try {
      setOperationLoading(true);
      await deleteContract(id);
      console.log('✅ Договор удален');
    } catch (error) {
      console.error('❌ Ошибка удаления договора:', error);
      alert('Ошибка при удалении договора. Попробуйте еще раз.');
    } finally {
      setOperationLoading(false);
    }
  };

  const handleStatusChange = async (id: string, status: Contract['status']) => {
    try {
      setOperationLoading(true);
      await updateContractStatus(id, status);
      console.log('✅ Статус договора изменен');
    } catch (error) {
      console.error('❌ Ошибка изменения статуса:', error);
      alert('Ошибка при изменении статуса договора. Попробуйте еще раз.');
    } finally {
      setOperationLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="app">
        <Navigation />
        <main className="main-content">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Загрузка договоров...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app">
        <Navigation />
        <main className="main-content">
          <div className="error-container">
            <h2>❌ Ошибка подключения</h2>
            <p>{error}</p>
            <p>Убедитесь, что сервер запущен (npm run server)</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <Navigation />
      <main className="main-content">
        {operationLoading && (
          <div className="operation-loading">
            <div className="loading-spinner small"></div>
            <span>Сохранение...</span>
          </div>
        )}
        <Routes>
          <Route 
            path="/" 
            element={<Dashboard contracts={contracts} />} 
          />
          <Route 
            path="/create" 
            element={
              <ContractForm 
                onSubmit={editingContract ? handleUpdateContract : handleCreateContract}
                initialData={editingContract || undefined}
                disabled={operationLoading}
              />
            } 
          />
          <Route 
            path="/history" 
            element={
              <ContractsList 
                contracts={contracts}
                onEdit={handleEditContract}
                onDelete={handleDeleteContract}
                onStatusChange={handleStatusChange}
                disabled={operationLoading}
              />
            } 
          />
        </Routes>
      </main>
    </div>
  );
};

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
